// Vendors
import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Breadcrumbs, Typography, Box } from '@mui/material';

// Components
import ContractEquipment from '@/components/molecules/Equipment/ContractEquipment/ContractEquipment';
import { InsuranceTab } from '@/components/molecules/InsuranceTab/InsuranceTab';
import NotesTab from '@/components/molecules/NotesTab/NotesTab';
import CustomTabs from '@/components/organism/Tabs/Tabs';

// Models and Store
import { BaseContract, Equipment, PolicyBilling } from '@/api/models/Contracts';
import { fetchCustomerContractStart } from '@/store/features/contracts/contractSlice';
import { useAppDispatch, useAppSelector } from '@/hooks/storeHook';
import { showBackdrop, hideBackdrop } from '@/store/features/ui/uiSlice';

// Constants
import {
  CONTRACT_TABS,
  CUSTOMER_CONTRACTS,
  POPUP_TYPES,
  ContractTabsLabels,
  POLICY_BILLING,
} from '@/utils/constants';
import CustomerContractListPopup from '@/components/molecules/ContractDetail/CustomerContractsPopup/CustomerContractsPopup';
import CustomerDetailsPopup from '@/components/molecules/ContractDetail/CustomerDetailPopup/CustomerDetailPopup';
import EquipmentDetailsPopup from '@/components/molecules/Equipment/EquipmentPopup/EquipmentPopup';
import { ContractDetails } from '@/utils/types/contractDetails';
import { ContractByCustomerRequest } from '@/api/models/BaseRequest';
import DeficiencyPopup from '@/components/molecules/Deficiency/DeficiencyPopup';
import { ExportNotesAction } from '@/utils/types/notesTab';
import PolicyBillingPopup from '@/components/molecules/PolicyBilling/PolicyBillingPopUp';
import AgentInfoPopup from '@/components/molecules/AgentInfo/AgentInfoPopup';
import { EventData } from '@/utils/types';

/**
 * Component: ContractDetailPage
 * Displays contract details and equipment.
 */
const ContractDetailPage = () => {
  const { contractId, clientId} = useParams();
  // Default tab state (Insurance tab is selected by default)
  const [selectedTab, setSelectedTab] = useState<number | string>(CONTRACT_TABS.INSURANCE);
  const [popupType, setPopupType] = useState<POPUP_TYPES | null>(null);
  const appDispatch = useAppDispatch();
  const [selectedEquipmentIndex, setSelectedEquipmentIndex] = useState<number>();
  const [deficiencyCodes, setDeficiencyCodes] = useState<string[]>([]);
  const [hasNotes, setHasNotes] = useState(false);
  const [policyBillingData, setPolicyBillingData] = useState<{
    title: string;
    subtitle: string;
    headers: string[];
    rows: string[][];
  } | null>(null);
  const [policyLoading, setPolicyLoading] = useState<boolean>(false);
  const [oiPolicyEvent, setOIPolicyEvent] = useState<EventData>();

  const handleContractListPopup = () => {
    setPopupType(POPUP_TYPES.CONTRACT_LIST);
    appDispatch(showBackdrop());
    window.dataLayer.push({
      event:'Contracts_popup_open',
      popup_name: 'View all contracts'
    });
  };

  const handleContractCustomerPopup = () => {
    setPopupType(POPUP_TYPES.CUSTOMER_DETAILS);
    appDispatch(showBackdrop());
    window.dataLayer.push({
      event:'Customer_details_popup_open',
      popup_name: 'View all customer details'
    })
  };

  const handleEquipmentPopup = (index: number) => {
    setSelectedEquipmentIndex(index);
    setPopupType(POPUP_TYPES.EQUIPMENT_DETAILS);
    appDispatch(showBackdrop());
  };

  const handlePolicyBillingClick = (
    policyNumber: string,
    billingItems: PolicyBilling[],
    loading: boolean,
  ) => {
    const billingData = {
      title: policyNumber ? `Policy: ${policyNumber} Billing Tree` : POLICY_BILLING.TITLE,
      subtitle: POLICY_BILLING.SUBTITLE,
      headers: POLICY_BILLING.HEADERS,
      rows: billingItems.map((item) => [
        item.policyEffective,
        item.policyExpiration,
        item.originalInvoice,
        item.originalPremium,
        item.invoiceDueDate,
      ]),
    };
    setPolicyLoading(loading);

    setPolicyBillingData(billingData);
    setPopupType(POPUP_TYPES.POLICY_BILLING_DETAILS);
    appDispatch(showBackdrop());
    window.dataLayer.push({
      event:'policy_billing',
      pop_name:'policy_billing_details'
    })
  };

  const closePopups = () => {
    setPopupType(null);
    appDispatch(hideBackdrop());
  };

  const { contractDetails } = useAppSelector(
    (state: { contractDetails: { contractDetails: ContractDetails | null } }) =>
      state.contractDetails,
  );

  const { equipments } = useAppSelector(
    (state: { equipments: { equipments: Equipment[] } }) => state.equipments,
  );

  const exportNotesRef = useRef<ExportNotesAction>(null);

  const handleExportNotes = () => {
    exportNotesRef.current?.handleExportNotes(); // Calls function in child
  };

  const { customerContracts } = useAppSelector(
    (state: {
      contracts: {
        customerContracts: BaseContract[];
        customerContractsLoading: boolean;
      };
    }) => state.contracts,
  );
  // Fetch customer contracts when `contractDetails` changes
  useEffect(() => {
    if (contractDetails && contractDetails.customerId) {
      var request: ContractByCustomerRequest = {
        clientId: contractDetails.clientId || '',
        customerId: contractDetails.customerId.toString(),
        keyword: '',
        pageNo: 0,
        pageSize: 0,
      };
      appDispatch(fetchCustomerContractStart(request));
    }
  }, [contractDetails, appDispatch]);

  const customerContractsData = {
    title: contractDetails ? contractDetails.customerName : CUSTOMER_CONTRACTS.TITLE,
    subtitle: CUSTOMER_CONTRACTS.SUBTITLE,
    headers: CUSTOMER_CONTRACTS.HEADERS,
    rows: customerContracts.map((contract) => [
      contract.contractId.toString(),
      contract.branch,
      contract.privateLabel,
      contract.cycle,
      contract.netBalance.toString(),
      contract.block,
      contract.bClass,
      contract.lClass,
      contract.cClass,
      contract.coll_Description,
      contract.serialNumber,
    ]),
  };

  const setTabSettings = (tabValue: number | string) => {
    setSelectedTab(tabValue);
    if (tabValue === 0) {
      setHasNotes(false);
    }
  };

  return (
    <Box className="contract-detail-page">
      {/* Flex Container to align Left and Right Sections */}
      <Box
        className="contractdetails-container"
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="2rem"
      >
        {/* Left Side: Breadcrumbs & Contract Details */}
        <Box className="contract_secondary">
          {/* Contract Details */}
          <Box className="contract-info_wrap">
            <div className="left-section_wrap">
              <Breadcrumbs aria-label="breadcrumb">
                <Link to="/search">Contracts</Link>
                <Typography color="textPrimary">Contract #: {contractId}</Typography>
              </Breadcrumbs>
              <ContractEquipment
                handleContractListPopup={handleContractListPopup}
                handleContractCustomerPopup={handleContractCustomerPopup}
                contractId={contractId?.toString()}
                clientId = {clientId?.toString()}
                handleEquipmentPopup={handleEquipmentPopup}
              />
            </div>
            <div className="right-section">
              {/* Tabs Component */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                className="tabs-header"
              >
                <CustomTabs
                  labels={ContractTabsLabels}
                  selectedTab={selectedTab}
                  onChange={(_, newValue) =>{ 
                    setTabSettings(newValue)
                    window.dataLayer.push({
                      event:'tabs_selected',
                      tab_name: selectedTab
                    })
                  }}
                />
                {selectedTab === CONTRACT_TABS.NOTES && hasNotes && (
                  <button className="export-notes-btn" onClick={handleExportNotes}>
                    Export Notes
                  </button>
                )}
              </Box>

              {selectedTab === CONTRACT_TABS.NOTES && contractId !== undefined && (
                <NotesTab
                  contractId={contractId}
                  clientId={clientId}
                  ref={exportNotesRef}
                  onNotesAvailabilityChange={setHasNotes}
                />
              )}
              {selectedTab === CONTRACT_TABS.INSURANCE && contractId !== undefined && (
                <InsuranceTab
                clientId={clientId}
                  contractId={contractId}
                  onDeficiencyClick={(codes) => {
                    setDeficiencyCodes(codes);
                    setPopupType(POPUP_TYPES.DEFICIENCY_DETAILS);
                    appDispatch(showBackdrop());
                  }}
                  onPolicyBillingClick={handlePolicyBillingClick}
                  onOIPolicyClick= {(event) => {
                    setOIPolicyEvent(event);
                    setPopupType(POPUP_TYPES.AGENT_DETAILS);
                     appDispatch(showBackdrop());
                  }}
                />
              )}
            </div>
          </Box>

          {popupType === POPUP_TYPES.DEFICIENCY_DETAILS && (
            <DeficiencyPopup isOpen={true} onClose={closePopups} deficiencyList={deficiencyCodes} />
          )}

          {popupType === POPUP_TYPES.POLICY_BILLING_DETAILS && (
            <PolicyBillingPopup
              isOpen={true}
              onClose={closePopups}
              data={policyBillingData}
              loading={policyLoading}
            />
          )}

          {popupType === POPUP_TYPES.CONTRACT_LIST && (
            <CustomerContractListPopup
              isOpen={true}
              onClose={closePopups}
              data={customerContractsData}
              clientId={clientId}
            />
          )}
          {popupType === POPUP_TYPES.CUSTOMER_DETAILS && contractDetails && (
            <CustomerDetailsPopup
              isOpen={true}
              onClose={closePopups}
              data={contractDetails}
              handleContractListClick={handleContractListPopup}
            />
          )}
          {popupType === POPUP_TYPES.EQUIPMENT_DETAILS && selectedEquipmentIndex != null && (
            <EquipmentDetailsPopup
              isOpen={true}
              onClose={closePopups}
              data={equipments[selectedEquipmentIndex]}
            />
          )}
           {popupType === POPUP_TYPES.AGENT_DETAILS && oiPolicyEvent && (
            <AgentInfoPopup
              isOpen={true}
              onClose={closePopups}
              data={oiPolicyEvent}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContractDetailPage;
